import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getAccountScopeUserSettings } from 'owa-session-store';
import createMultiAccountStore from 'owa-viewstate-store/lib/createMultiAccountStore';
import { mutatorAction } from 'satcheljs';

import type { AccountFeatureName } from 'owa-feature-flags';
import type { MailboxInfo } from 'owa-client-types';
import type { PlacesSettings } from './types/PlacesSettings';

const LINKEDIN_DOMAINS = ['linkedin.com', 'linkedin.biz', 'internal.linkedin.cn'];

const MICROSOFT_TENANT_ID = '72f988bf-86f1-41af-91ab-2d7cd011db47';

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function createMultiAccountStore called at module scope is not allowed. This may cause unwanted side effects. */
const [read, write] = createMultiAccountStore('PLACES_SETTINGS', {
    loading: true,
    failed: false,
    reason: undefined as string | undefined,
    isOffline: false as boolean | undefined,
    placesWebAppEnabled: false,
    placesPremiumEnabled: false,
    placesPremiumEnabledFromSettings: false,
    placesCopilotEnabled: false,
    placesFinderEnabled: false,
});

export function isLoading(mailboxInfo: MailboxInfo): boolean {
    const store = read(mailboxInfo);
    return getValue(
        mailboxInfo,
        'places-dev-settings-loading-enabled',
        'places-dev-settings-loading-disabled',
        store.loading
    );
}

export function hasFailed(mailboxInfo: MailboxInfo): boolean {
    const store = read(mailboxInfo);
    return getValue(
        mailboxInfo,
        'places-dev-settings-failed-enabled',
        'places-dev-settings-failed-disabled',
        store.failed
    );
}

export function hasLoaded(mailboxInfo: MailboxInfo): boolean {
    const store = read(mailboxInfo);
    return !store.loading && !store.failed;
}

export function isPlacesWebAppEnabled(mailboxInfo: MailboxInfo): boolean {
    const store = read(mailboxInfo);
    return getValue(
        mailboxInfo,
        'places-dev-settings-webapp-enabled',
        'places-dev-settings-webapp-disabled',
        store.placesWebAppEnabled
    );
}

export function isPlacesPremiumEnabled(
    mailboxInfo: MailboxInfo,
    useValueFromSetting?: boolean
): boolean {
    const store = read(mailboxInfo);
    return getValue(
        mailboxInfo,
        'places-dev-settings-premium-enabled',
        'places-dev-settings-premium-disabled',
        useValueFromSetting ? store.placesPremiumEnabledFromSettings : store.placesPremiumEnabled
    );
}

export function isPlacesCopilotEnabled(mailboxInfo: MailboxInfo): boolean {
    const store = read(mailboxInfo);
    return (
        isPlacesPremiumEnabled(mailboxInfo) &&
        getValue(
            mailboxInfo,
            'places-dev-settings-copilot-enabled',
            'places-dev-settings-copilot-disabled',
            store.placesCopilotEnabled
        )
    );
}

export function isPlacesFinderEnabled(mailboxInfo: MailboxInfo): boolean {
    const store = read(mailboxInfo);
    const tenantSettingEnabled =
        isPlacesPremiumEnabled(mailboxInfo) &&
        getValue(
            mailboxInfo,
            'places-dev-settings-finder-enabled',
            'places-dev-settings-finder-disabled',
            store.placesFinderEnabled
        );
    return (
        !isLinkedInUser(mailboxInfo) &&
        (tenantSettingEnabled || isAccountFeatureEnabled('cal-use-places-finder', mailboxInfo))
    );
}

export function isOpxPlacesFinderEnabled(mailboxInfo: MailboxInfo): boolean {
    const store = read(mailboxInfo);
    const tenantSettingEnabled =
        isPlacesPremiumEnabled(mailboxInfo) &&
        getValue(
            mailboxInfo,
            'places-dev-settings-finder-enabled',
            'places-dev-settings-finder-disabled',
            store.placesFinderEnabled
        );
    return (
        !isLinkedInUser(mailboxInfo) &&
        (tenantSettingEnabled || isAccountFeatureEnabled('cal-opx-use-places-finder', mailboxInfo))
    );
}

function getValue(
    mailboxInfo: MailboxInfo,
    enabled: AccountFeatureName,
    disabled: AccountFeatureName,
    storeValue: boolean
) {
    return isAccountFeatureEnabled(enabled, mailboxInfo)
        ? true
        : isAccountFeatureEnabled(disabled, mailboxInfo)
        ? false
        : storeValue;
}

function isLinkedInUser(mailboxInfo: MailboxInfo) {
    if (isGlobalFeatureEnabled('msplaces-disable-linkedin-user-block')) {
        return false;
    }

    const sessionSettings = getAccountScopeUserSettings(mailboxInfo)?.SessionSettings;

    return (
        sessionSettings?.ExternalDirectoryTenantGuid == MICROSOFT_TENANT_ID &&
        LINKEDIN_DOMAINS.some(x => x == sessionSettings?.TenantDomain?.toLowerCase())
    );
}

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const resetPlacesSettings: (mailboxInfo: MailboxInfo) => void = mutatorAction(
    'RESET_PLACES_SETTINGS',
    (mailboxInfo: MailboxInfo): void => {
        const store = write(mailboxInfo);
        store.loading = true;
        store.failed = false;
        store.reason = undefined;
        store.isOffline = false;
        store.placesWebAppEnabled = false;
        store.placesPremiumEnabled = false;
        store.placesPremiumEnabledFromSettings = false;
        store.placesCopilotEnabled = false;
        store.placesFinderEnabled = false;
    }
);

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const setPlacesSettings: (mailboxInfo: MailboxInfo, newValues: PlacesSettings) => void =
    mutatorAction(
        'SET_PLACES_SETTINGS',
        (mailboxInfo: MailboxInfo, newValues: PlacesSettings): void => {
            const store = write(mailboxInfo);
            store.loading = false;
            store.failed = false;
            store.reason = newValues.reason;
            store.isOffline = newValues.isOffline;
            store.placesWebAppEnabled = newValues.placesWebAppEnabled;
            store.placesPremiumEnabled = newValues.placesPremiumEnabled;
            store.placesPremiumEnabledFromSettings = newValues.placesPremiumEnabledFromSettings;
            store.placesCopilotEnabled = newValues.placesCopilotEnabled;
            store.placesFinderEnabled = newValues.placesFinderEnabled;
        }
    );

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
export const setPlacesSettingsFailed: (mailboxInfo: MailboxInfo, fallback: PlacesSettings) => void =
    mutatorAction(
        'SET_PLACES_SETTINGS_FAILED',
        (mailboxInfo: MailboxInfo, fallback: PlacesSettings): void => {
            const store = write(mailboxInfo);
            store.loading = false;
            store.failed = true;
            store.reason = fallback.reason;
            store.isOffline = fallback.isOffline;
            store.placesWebAppEnabled = fallback.placesWebAppEnabled;
            store.placesPremiumEnabled = fallback.placesPremiumEnabled;
            store.placesPremiumEnabledFromSettings = fallback.placesPremiumEnabledFromSettings;
            store.placesCopilotEnabled = fallback.placesCopilotEnabled;
            store.placesFinderEnabled = fallback.placesFinderEnabled;
        }
    );

export function getPlacesSettings(mailboxInfo: MailboxInfo): PlacesSettings {
    const store = read(mailboxInfo);
    return {
        reason: store.reason,
        isOffline: store.isOffline,
        placesWebAppEnabled: store.placesWebAppEnabled,
        placesPremiumEnabled: store.placesPremiumEnabled,
        placesPremiumEnabledFromSettings: store.placesPremiumEnabledFromSettings,
        placesCopilotEnabled: store.placesCopilotEnabled,
        placesFinderEnabled: store.placesFinderEnabled,
    };
}
